import type { ReactNode } from 'react'
import { CalendarDays, Timer, Truck } from 'lucide-react'
import Badge, { type BadgeTone } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'
import { daysUntil, formatDate, formatWeekday } from '@/lib/date'
import type { DepartmentDeadline, Job } from '@/types'

/* Icewall icon chips in the badge's tones, as StatTile does, so the chip and the countdown beside it agree. */
const chipTones: Record<BadgeTone, string> = {
  neutral: 'border-foreground/30 bg-foreground/10 text-foreground/80',
  brand: 'border-primary/60 bg-primary/20 text-primary',
  success: 'border-success/60 bg-success/20 text-success',
  warning: 'border-warning/60 bg-warning/20 text-warning',
  danger: 'border-danger/60 bg-danger/20 text-danger',
  info: 'border-pending/60 bg-pending/20 text-pending',
}

interface Tile {
  label: string
  date?: string | null
  icon: ReactNode
  tone: BadgeTone
  status?: string
  /** Paints the date itself red: a section running late or a delivery overdue. */
  alert?: boolean
}

/**
 * The three dates a job is worked to, above the order and stage cards on the
 * staff and partner job pages: when production starts, when the section in
 * hand is booked to finish, and when the order is due out. They used to be one
 * grey line under the items, which is how a due date gets missed.
 *
 * Three tiles in one row at every width, so on a phone the dates take one
 * band of the screen rather than three. Below `sm` a tile is under 100px wide:
 * the type steps down, labels and long countdowns wrap, and every label keeps
 * two lines of height so the dates line up whichever labels wrapped.
 * The icon chip only shows from `lg`, where it fits beside a full-size date.
 */
export default function JobDateStrip({ job, deadlines }: { job: Job; deadlines: DepartmentDeadline[] }) {
  const order = job.order
  if (!order) return null

  const tiles = [
    productionTile(job, order.production_date),
    sectionTile(job, deadlines),
    deliveryTile(job, order.expected_completion_date),
  ]

  return (
    <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-4">
      {tiles.map((tile, index) => (
        <div key={index} className="box flex gap-4 px-2 py-2.5 sm:p-5">
          <span
            className={cn('hidden size-11 shrink-0 items-center justify-center rounded-full border lg:flex', chipTones[tile.tone])}
          >
            {tile.icon}
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="min-h-7 text-2xs font-semibold uppercase leading-3.5 text-foreground/60 sm:min-h-0 sm:truncate sm:text-xs sm:leading-4 sm:tracking-wide">
              {tile.label}
            </p>
            <p
              className={cn(
                'mt-1 text-[0.8125rem] font-semibold tabular-nums sm:text-xl',
                tile.alert ? 'text-danger' : 'text-foreground',
              )}
            >
              {formatDate(tile.date)}
            </p>
            <div className="flex flex-col items-start gap-1 sm:mt-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2">
              <p className="text-2xs text-muted sm:text-xs">{formatWeekday(tile.date)}</p>
              {tile.status && (
                <Badge tone={tile.tone} className="leading-tight max-sm:px-1.5 max-sm:text-2xs">
                  {tile.status}
                </Badge>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function productionTile(job: Job, date: string): Tile {
  const days = daysUntil(date)
  const tile = { label: 'Production', date, icon: <CalendarDays className="size-5" /> }

  if (isFinished(job) || days === null) return { ...tile, tone: 'neutral' }
  if (days < 0) return { ...tile, tone: 'neutral', status: `${dayCount(days)} ago` }

  return { ...tile, tone: 'brand', status: countdown(days) }
}

/** The current section's booked day. The server's `late` flag decides red, the same rule the job lists use. */
function sectionTile(job: Job, deadlines: DepartmentDeadline[]): Tile {
  const icon = <Timer className="size-5" />

  if (job.status === 'cancelled') return { label: 'Section due', icon, tone: 'neutral', status: 'Cancelled' }

  const section = job.current_department
  if (!section) {
    const last = deadlines[deadlines.length - 1]
    return { label: 'All sections', date: last?.completed_at ?? last?.date, icon, tone: 'success', status: 'Done' }
  }

  const label = `${section.name} due`
  const deadline = deadlines.find((d) => d.department_id === section.id)
  const days = daysUntil(deadline?.date)

  if (!deadline || days === null) return { label, icon, tone: 'neutral' }

  // A section that has part finished its pieces says what it still owes. The
  // balance is why the day moved, so it reads as deliberate rather than as
  // slippage. Just the balance, no total: on a phone the tile is under 100px
  // wide, so "300 of 450 pcs left" wrapped to two lines and pushed this tile
  // taller than the two beside it. The order total is in the card below.
  const done = deadline.pieces_done ?? 0
  const balance = deadline.pieces !== null && deadline.pieces !== undefined ? deadline.pieces - done : null
  if (done > 0 && balance !== null && balance > 0) {
    return { label, date: deadline.date, icon, tone: 'warning', status: `${balance} left` }
  }

  if (deadline.late) {
    return { label, date: deadline.date, icon, tone: 'danger', alert: true, status: days < 0 ? `${dayCount(days)} late` : 'Late' }
  }

  return { label, date: deadline.date, icon, tone: urgency(days), status: countdown(Math.max(days, 0)) }
}

function deliveryTile(job: Job, date: string): Tile {
  const days = daysUntil(date)
  const tile = { label: 'Delivery due', date, icon: <Truck className="size-5" /> }

  if (job.status === 'cancelled' || days === null) return { ...tile, tone: 'neutral' }
  if (job.status === 'completed') return { ...tile, tone: 'success', status: 'Production done' }
  if (days < 0) return { ...tile, tone: 'danger', alert: true, status: `${dayCount(days)} overdue` }

  return { ...tile, tone: urgency(days), status: countdown(days) }
}

function isFinished(job: Job): boolean {
  return job.status === 'completed' || job.status === 'cancelled'
}

/** For today or later: "Today", "Tomorrow", "in N days". */
function countdown(days: number): string {
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'

  return `in ${days} days`
}

function dayCount(days: number): string {
  const n = Math.abs(days)

  return `${n} day${n === 1 ? '' : 's'}`
}

/** Brand while there is time, warning inside two days. */
function urgency(days: number): BadgeTone {
  return days <= 2 ? 'warning' : 'brand'
}
